import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { createDataset, deleteDataset, getAllDatasets, getDatasetById, updateDataset } from '@/api/datasets';
import { appLogger } from '@/utils/logger';

export const useDatasets = (id?: string) => {
  return useQuery({
    queryKey: ['datasets', id], // Clave única
    queryFn: () => {
      appLogger.info('Fetching all datasets', { id });
      return getAllDatasets(id);
    },
    staleTime: 1000 * 60 * 5, // Evitar que se vuelva a buscar por 5 minutos
    retry: 2, // Número de reintentos si falla
    enabled: !!id, // Solo ejecuta si id está definido
  });
};

export const useDatasetById = (id?: string) => {
  return useQuery({
    queryKey: ['dataset', id],
    queryFn: async () => {
      appLogger.info('Fetching dataset by id', { id });
      return getDatasetById(id);
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
    enabled: !!id, // Solo ejecuta si id está definido
  });
};

export const useCreateDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      appLogger.info('Creating dataset', data);
      return createDataset(data);
    },
    onError: (err) => {
      appLogger.error('Error creating dataset', err);
      notification.error({
        message: err.message || "We couldn't create the dataset",
        className: 'custom-error-notification bold-notification-message',
        pauseOnHover: true,
      });
    },
    onSuccess: (data) => {
      appLogger.info('Dataset created successfully', data);
      queryClient.invalidateQueries({ queryKey: ['datasets'] });

      notification.success({
        message: 'Dataset created successfully',
        className: 'custom-success-notification bold-notification-message',
        pauseOnHover: true,
      });
    },
  });
};

export const useUpdateDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { id: string; name: string }) => {
      appLogger.info('Updating dataset', data);
      return updateDataset(data);
    },
    onError: (err) => {
      appLogger.error('Error updating dataset', err);
      notification.error({
        message: err?.message || "We couldn't update the dataset",
        className: 'custom-error-notification bold-notification-message',
        pauseOnHover: true,
      });
    },
    onSuccess: (data) => {
      appLogger.info('Dataset updated successfully', data);
      queryClient.invalidateQueries({ queryKey: ['datasets'] });

      notification.success({
        message: 'Dataset updated successfuly',
        className: 'custom-success-notification bold-notification-message',
        pauseOnHover: true,
      });
    },
  });
};

export const useDeleteDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      appLogger.info('Deleting dataset', { id });
      return deleteDataset(id);
    },
    onError: (err) => {
      appLogger.error('Error deleting dataset', err);
      notification.error({
        message: err?.message || 'Could not delete the dataset.',
        className: 'custom-error-notification bold-notification-message',
        pauseOnHover: true,
      });
    },
    onSuccess: (data) => {
      appLogger.info('Dataset deleted successfully', data);
      queryClient.invalidateQueries({ queryKey: ['datasets'] });

      notification.success({
        message: 'Dataset deleted successfully!',
        className: 'custom-success-notification bold-notification-message',
        pauseOnHover: true,
      });
    },
  });
};
